import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import type {
  ContentRecommendation,
  MasteryState,
  BehaviorPattern,
  ContextualFactor,
} from '@coffeeplexity/shared';
import type {
  FilteredContent,
  ContextualSuggestion,
  AdaptiveFilteringConfig,
} from '../../services/adaptiveContentFiltering';
import { adaptiveFilteringService } from '../../services/adaptiveContentFiltering';
import { contentDiscoveryAnalytics } from '../../services/contentDiscoveryAnalytics';
import { contentRecommendationABTesting } from '../../services/contentRecommendationABTesting';
import { AIRecommendationCard } from './AIRecommendationCard';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { RecommendationFilters } from './RecommendationFilters';
import { styles } from './AdaptiveContentFiltering.styles';
import { logger } from '../../utils/logger';

interface AdaptiveContentFilteringProps {
  userId: string;
  initialContent: ContentRecommendation[];
  masteryStates: MasteryState[];
  behaviorPatterns: BehaviorPattern[];
  contextualFactors: ContextualFactor[];
  currentContentId?: string;
  onContentSelect: (content: ContentRecommendation) => void;
  onFilterChange?: (filters: any) => void;
  showAnalytics?: boolean;
  enableABTesting?: boolean;
  maxRecommendations?: number;
}

interface FilteringState {
  filteredContent: FilteredContent | null;
  contextualSuggestions: ContextualSuggestion[];
  isLoading: boolean;
  error: string | null;
  appliedFilters: any[];
  abTestAssignments: any[];
}

export const AdaptiveContentFiltering: React.FC<
  AdaptiveContentFilteringProps
> = ({
  userId,
  initialContent,
  masteryStates,
  behaviorPatterns,
  contextualFactors,
  currentContentId,
  onContentSelect,
  onFilterChange,
  showAnalytics = false,
  enableABTesting = true,
  maxRecommendations = 20,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const { activeRecommendations, loading } = useSelector(
    (state: RootState) => state.recommendationEngine
  );

  // Local state
  const [filteringState, setFilteringState] = useState<FilteringState>({
    filteredContent: null,
    contextualSuggestions: [],
    isLoading: false,
    error: null,
    appliedFilters: [],
    abTestAssignments: [],
  });

  const [selectedFilters, setSelectedFilters] = useState<any>({
    confidenceThreshold: 0.7,
    difficultyRange: { min: 0, max: 10 },
    contentTypes: [],
    showExplanations: true,
  });

  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  // Memoized content to filter
  const contentToFilter = useMemo(() => {
    return initialContent.length > 0 ? initialContent : activeRecommendations;
  }, [initialContent, activeRecommendations]);

  /**
   * Apply adaptive filtering to content
   */
  const applyAdaptiveFiltering = useCallback(async () => {
    if (contentToFilter.length === 0) return;

    setFilteringState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Get A/B test assignments if enabled
      let abTestAssignments: any[] = [];
      if (enableABTesting) {
        abTestAssignments = contentRecommendationABTesting.getUserAssignment(
          userId,
          masteryStates,
          behaviorPatterns,
          {
            deviceType: 'mobile',
            appVersion: '1.0.0',
          }
        );
      }

      // Apply mastery-based filtering
      const filteredResult =
        await adaptiveFilteringService.applyMasteryBasedFiltering(
          contentToFilter,
          masteryStates,
          selectedFilters.confidenceThreshold
        );

      // Apply engagement-based ranking
      const rankedContent =
        await adaptiveFilteringService.rankContentByEngagement(
          filteredResult.content,
          userId,
          behaviorPatterns
        );

      // Apply A/B test variants if assigned
      let finalContent = rankedContent;
      for (const assignment of abTestAssignments) {
        finalContent = contentRecommendationABTesting.applyVariant(
          finalContent,
          assignment.variantId,
          assignment.experimentId
        );
      }

      // Limit to max recommendations
      finalContent = finalContent.slice(0, maxRecommendations);

      // Generate contextual suggestions if current content is provided
      let contextualSuggestions: ContextualSuggestion[] = [];
      if (currentContentId) {
        contextualSuggestions =
          await adaptiveFilteringService.generateContextualSuggestions(
            currentContentId,
            userId,
            contextualFactors,
            contentToFilter
          );
      }

      // Update state
      setFilteringState({
        filteredContent: {
          ...filteredResult,
          content: finalContent,
        },
        contextualSuggestions,
        isLoading: false,
        error: null,
        appliedFilters: filteredResult.appliedFilters,
        abTestAssignments,
      });

      // Track analytics event
      if (analyticsEnabled) {
        contentDiscoveryAnalytics.trackEvent({
          userId,
          eventType: 'filter_applied',
          sessionId: `session_${userId}_${Date.now()}`,
          context: {
            screenName: 'AdaptiveContentFiltering',
            sectionName: 'recommendations',
            position: 0,
            totalItems: finalContent.length,
            userContext: {
              skillLevel: getAverageSkillLevel(masteryStates),
              journeyStage: 'active',
              sessionDuration: 0,
              previousActions: [],
            },
          },
          metadata: {
            filtersApplied: filteredResult.appliedFilters.length,
            contentFiltered: contentToFilter.length - finalContent.length,
            confidenceScore: filteredResult.confidenceScore,
            abTestVariants: abTestAssignments.map(a => a.variantId),
          },
        });
      }

      // Notify parent of filter changes
      onFilterChange?.(selectedFilters);
    } catch (error) {
      logger.error('Error in adaptive filtering:', error);
      setFilteringState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Filtering failed',
      }));
    }
  }, [
    contentToFilter,
    masteryStates,
    behaviorPatterns,
    contextualFactors,
    selectedFilters,
    userId,
    currentContentId,
    enableABTesting,
    maxRecommendations,
    analyticsEnabled,
    onFilterChange,
  ]);

  /**
   * Handle content selection
   */
  const handleContentSelect = useCallback(
    (content: ContentRecommendation) => {
      // Track analytics
      if (analyticsEnabled) {
        contentDiscoveryAnalytics.trackContentInteraction(
          userId,
          content.contentId,
          'click',
          {
            screenName: 'AdaptiveContentFiltering',
            sectionName: 'recommendations',
            position:
              filteringState.filteredContent?.content.findIndex(
                c => c.id === content.id
              ) || 0,
            totalItems: filteringState.filteredContent?.content.length || 0,
            userContext: {
              skillLevel: getAverageSkillLevel(masteryStates),
              journeyStage: 'active',
              sessionDuration: 0,
              previousActions: [],
            },
          },
          {
            confidence: content.confidence,
            appliedFilters: filteringState.appliedFilters.map(f => f.type),
            abTestVariants: filteringState.abTestAssignments.map(
              a => a.variantId
            ),
          }
        );

        // Track A/B test events
        for (const assignment of filteringState.abTestAssignments) {
          contentRecommendationABTesting.trackEvent(userId, 'content_click', {
            contentId: content.contentId,
            position: filteringState.filteredContent?.content.findIndex(
              c => c.id === content.id
            ),
            confidence: content.confidence,
          });
        }
      }

      onContentSelect(content);
    },
    [userId, analyticsEnabled, masteryStates, filteringState, onContentSelect]
  );

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((newFilters: any) => {
    setSelectedFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Handle contextual suggestion selection
   */
  const handleSuggestionSelect = useCallback(
    (suggestion: ContextualSuggestion) => {
      const content = contentToFilter.find(
        c => c.contentId === suggestion.contentId
      );
      if (content) {
        // Track suggestion acceptance
        if (analyticsEnabled) {
          contentDiscoveryAnalytics.trackEvent({
            userId,
            eventType: 'suggestion_accepted',
            sessionId: `session_${userId}_${Date.now()}`,
            context: {
              screenName: 'AdaptiveContentFiltering',
              sectionName: 'contextual_suggestions',
              position: filteringState.contextualSuggestions.findIndex(
                s => s.id === suggestion.id
              ),
              totalItems: filteringState.contextualSuggestions.length,
              userContext: {
                skillLevel: getAverageSkillLevel(masteryStates),
                journeyStage: 'active',
                sessionDuration: 0,
                previousActions: [],
              },
            },
            metadata: {
              suggestionType: suggestion.type,
              confidence: suggestion.confidence,
              contextFactors: suggestion.contextFactors,
            },
          });
        }

        handleContentSelect(content);
      }
    },
    [
      contentToFilter,
      userId,
      analyticsEnabled,
      filteringState,
      handleContentSelect,
    ]
  );

  /**
   * Toggle analytics
   */
  const toggleAnalytics = useCallback(() => {
    setAnalyticsEnabled(prev => !prev);
  }, []);

  /**
   * Show filtering details
   */
  const showFilteringDetails = useCallback(() => {
    if (!filteringState.filteredContent) return;

    const details = [
      `Applied ${filteringState.appliedFilters.length} filters`,
      `Confidence: ${(filteringState.filteredContent.confidenceScore * 100).toFixed(1)}%`,
      `Showing ${filteringState.filteredContent.content.length} of ${contentToFilter.length} items`,
      ...filteringState.filteredContent.reasoning,
    ];

    if (filteringState.abTestAssignments.length > 0) {
      details.push(
        `A/B Test Variants: ${filteringState.abTestAssignments.map(a => a.variantId).join(', ')}`
      );
    }

    Alert.alert('Filtering Details', details.join('\n\n'));
  }, [filteringState, contentToFilter.length]);

  // Apply filtering when dependencies change
  useEffect(() => {
    applyAdaptiveFiltering();
  }, [applyAdaptiveFiltering]);

  // Render loading state
  if (filteringState.isLoading || loading.recommendations) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size='large' color='#8B4513' />
        <Text style={styles.loadingText}>
          Applying intelligent filtering...
        </Text>
      </View>
    );
  }

  // Render error state
  if (filteringState.error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Filtering Error</Text>
        <Text style={styles.errorMessage}>{filteringState.error}</Text>
        <TouchableOpacity
          style={styles.retryButton}
          onPress={applyAdaptiveFiltering}
        >
          <Text style={styles.retryButtonText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const { filteredContent, contextualSuggestions } = filteringState;

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Filter Controls */}
      <View style={styles.filtersSection}>
        <RecommendationFilters
          filters={selectedFilters}
          onFilterChange={handleFilterChange}
          masteryStates={masteryStates}
          showAdvanced={true}
        />
      </View>

      {/* Filtering Status */}
      {filteredContent && (
        <View style={styles.statusSection}>
          <View style={styles.statusHeader}>
            <Text style={styles.statusTitle}>
              {filteredContent.content.length} Recommendations
            </Text>
            <ConfidenceIndicator
              confidence={filteredContent.confidenceScore}
              size='small'
              showLabel={true}
            />
          </View>

          {showAnalytics && (
            <View style={styles.analyticsControls}>
              <TouchableOpacity
                style={styles.detailsButton}
                onPress={showFilteringDetails}
              >
                <Text style={styles.detailsButtonText}>View Details</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.analyticsToggle,
                  analyticsEnabled && styles.analyticsToggleActive,
                ]}
                onPress={toggleAnalytics}
              >
                <Text
                  style={[
                    styles.analyticsToggleText,
                    analyticsEnabled && styles.analyticsToggleTextActive,
                  ]}
                >
                  Analytics {analyticsEnabled ? 'ON' : 'OFF'}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {filteredContent.fallbackUsed && (
            <View style={styles.fallbackNotice}>
              <Text style={styles.fallbackText}>
                Using fallback recommendations due to filtering constraints
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Contextual Suggestions */}
      {contextualSuggestions.length > 0 && (
        <View style={styles.suggestionsSection}>
          <Text style={styles.sectionTitle}>Contextual Suggestions</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {contextualSuggestions.map(suggestion => (
              <TouchableOpacity
                key={suggestion.id}
                style={styles.suggestionCard}
                onPress={() => handleSuggestionSelect(suggestion)}
              >
                <Text style={styles.suggestionType}>
                  {suggestion.type.replace(/_/g, ' ')}
                </Text>
                <Text style={styles.suggestionTitle}>{suggestion.title}</Text>
                <Text style={styles.suggestionDescription} numberOfLines={2}>
                  {suggestion.description}
                </Text>
                <ConfidenceIndicator
                  confidence={suggestion.confidence}
                  size='small'
                  showLabel={false}
                />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Filtered Recommendations */}
      {filteredContent && (
        <View style={styles.recommendationsSection}>
          <Text style={styles.sectionTitle}>Personalized Recommendations</Text>
          {filteredContent.content.map((recommendation, index) => (
            <AIRecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
              onPress={() => handleContentSelect(recommendation)}
              onFeedback={feedback => {
                // Handle feedback
                if (analyticsEnabled) {
                  contentDiscoveryAnalytics.trackEvent({
                    userId,
                    eventType: 'content_rate',
                    contentId: recommendation.contentId,
                    sessionId: `session_${userId}_${Date.now()}`,
                    context: {
                      screenName: 'AdaptiveContentFiltering',
                      sectionName: 'recommendations',
                      position: index,
                      totalItems: filteredContent.content.length,
                      userContext: {
                        skillLevel: getAverageSkillLevel(masteryStates),
                        journeyStage: 'active',
                        sessionDuration: 0,
                        previousActions: [],
                      },
                    },
                    metadata: {
                      rating: feedback.rating,
                      feedbackType: feedback.feedbackType,
                    },
                  });
                }
              }}
              showExplanation={selectedFilters.showExplanations}
              showConfidence={true}
              style={styles.recommendationCard}
            />
          ))}
        </View>
      )}

      {/* Empty State */}
      {filteredContent && filteredContent.content.length === 0 && (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateTitle}>No Recommendations Found</Text>
          <Text style={styles.emptyStateMessage}>
            Try adjusting your filters or check back later for new content.
          </Text>
          <TouchableOpacity
            style={styles.resetFiltersButton}
            onPress={() =>
              setSelectedFilters({
                confidenceThreshold: 0.5,
                difficultyRange: { min: 0, max: 10 },
                contentTypes: [],
                showExplanations: true,
              })
            }
          >
            <Text style={styles.resetFiltersButtonText}>Reset Filters</Text>
          </TouchableOpacity>
        </View>
      )}
    </ScrollView>
  );
};

/**
 * Helper function to calculate average skill level
 */
function getAverageSkillLevel(masteryStates: MasteryState[]): string {
  if (masteryStates.length === 0) return 'beginner';

  const avgMastery =
    masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
    masteryStates.length;

  if (avgMastery < 0.3) return 'beginner';
  if (avgMastery < 0.7) return 'intermediate';
  return 'advanced';
}

export default AdaptiveContentFiltering;
